import { zodResolver } from '@hookform/resolvers/zod';
import {
  ButtonComponentActionResultTypenames,
  ButtonComponentV2,
  ProcessTypeInput,
} from '@varo/graphql-schema-types';
import {
  Href,
  NativeStackHeaderProps,
  Stack,
  useGlobalSearchParams,
  useRouter,
} from 'expo-router';
import useCancelTabapayTransfer from 'features/moveMoney/screens/dcf/hooks/useCancelTabapayTransfer';
import { useLogGQLErrorScreenAnalytics } from 'features/moveMoney/screens/dcf/hooks/useLogGQLErrorScreenAnalytics';
import { useThreeDsHandler } from 'features/moveMoney/screens/dcf/hooks/useThreeDsHandler';
import useThreeDsSdkWeb from 'features/moveMoney/screens/dcf/hooks/useThreeDsSdkWeb';
import {
  getFlowNameForAnalytics,
  isUserCancelled3DS,
  TabaPayEntryFlowType,
} from 'features/moveMoney/screens/dcf/tabapay/helpers/TabaPay.helpers';
import VerifyCardScreen from 'features/moveMoney/screens/dcf/tabapay/VerifyCardScreen';
import useExternalCardDetails from 'features/myVaro/screens/ExternalAccounts/hooks/useExternalCardDetails';
import useUpdateExternalCardExpiryDate from 'features/myVaro/screens/ExternalAccounts/hooks/useUpdateExternalCardExpiryDate';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Keyboard,
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { parseGQLAnalyticsEvent } from 'services/analytics/helpers/parseGQLAnalyticsEvent';
import Button from 'shared/components/vxl/Button/Button';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import { IllustrationKeyType } from 'shared/components/vxl/Illustration/Illustration';
import { expirationDateSchema } from 'shared/components/vxl/input/schemas/card/expirationDate.schema';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isIOS, isWeb } from 'shared/constants/platform';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useKeyboardVisibility from 'shared/hooks/keyboard/useKeyboardVisibility';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { getKeyboardVerticalOffset } from 'shared/utils/keyboardUtils';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { formatCardExpirationDate } from 'shared/utils/string/cardExpirationDate';
import { z } from 'zod';

const updateExpiryDateSchema = z.object({
  expiryDate: expirationDateSchema,
});

export default function UpdateExpiryDate() {
  const router = useRouter();
  const { cardId, from, tabaPayEntryFlowType } = useGlobalSearchParams<{
    cardId: string;
    from: string;
    tabaPayEntryFlowType: TabaPayEntryFlowType;
  }>();
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'updateExpiryDate',
  });
  const flowNameForAnalytics = getFlowNameForAnalytics(tabaPayEntryFlowType);
  useViewDidFocusScreenAnalyticsEvent({
    key: 'external-card-update-expiry-date',
    customAttributes: {
      flow_name: flowNameForAnalytics,
    },
  });
  const {
    startThreeDsWebFlow,
    isReady,
    errorScreenThreeDs: webError,
    isLoading: webLoading,
    initTransactionId: initTransactionIdWeb,
  } = useThreeDsSdkWeb();
  const { bottom, top } = useSafeAreaInsets();
  const { handleThreeDS, errorScreenThreeDs, initTransactionId } =
    useThreeDsHandler();
  const { updateExpiryDate, errorScreen, isLoading } =
    useUpdateExternalCardExpiryDate();
  const {
    cancelTabapayTransfer,
    isLoading: cancelTransferLoading,
    isSuccess,
    errorScreen: cancelTransferErrorScreen,
  } = useCancelTabapayTransfer();

  useEffect(() => {
    if (isSuccess) {
      navigateBack();
    }
  }, [isSuccess]);
  const combinedErrorScreen =
    webError || errorScreen || errorScreenThreeDs || cancelTransferErrorScreen;

  const isKeyboardVisible = useKeyboardVisibility();
  const { height } = useWindowDimensions();

  const [threeDsLoading, setThreeDsLoading] = useState(false);

  const {
    control,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      expiryDate: '',
    },
    resolver: zodResolver(updateExpiryDateSchema),
  });

  const flowName = getFlowNameForAnalytics(tabaPayEntryFlowType);

  const analyticsCommonAttributes = {
    subcategory: flowName,
    flow_name: flowName,
  };

  const updateCardExpirationDateErrorEvent = usePressableAnalytics({
    analyticsId: 'UpdateCardExpirationDateError',
    customAttributes: {
      error_name: errorScreen?.messageHeadline,
      error_description: errorScreen?.messageBody,
      ...analyticsCommonAttributes,
    },
  });

  const expiryDateErrorEncountered = usePressableAnalytics({
    analyticsId: 'debitCardInlineErrorEncountered',
    customAttributes: {
      validation_type: 'expiration_date_validation',
      customer_message: errors.expiryDate?.message,
      ...analyticsCommonAttributes,
    },
  });
  const handleExpiryDateErrorTriggered = useCallback(() => {
    if (errors.expiryDate?.message) {
      expiryDateErrorEncountered();
    }
  }, [errors.expiryDate?.message, expiryDateErrorEncountered]);

  useEffect(() => {
    if (errorScreen) {
      updateCardExpirationDateErrorEvent();
    }
  }, [errorScreen, updateCardExpirationDateErrorEvent]);

  useEffect(() => {
    if (combinedErrorScreen) {
      setThreeDsLoading(false);
    }
  }, [combinedErrorScreen]);

  const onSubmit = async () => {
    const { expiryDate } = getValues();
    Keyboard.dismiss();
    if (!isWeb) {
      try {
        setIsVerifyCardScreenShown(false);
        setThreeDsLoading(true);
        const threeDsResult = await handleThreeDS(
          ProcessTypeInput.CardUpdation,
          flowName,
          cardId,
        );
        if (threeDsResult) {
          await updateExpiryDate(cardId, expiryDate.toString(), from);
        }
        setThreeDsLoading(false);
      } catch (error) {
        logErrorWithContext(error, {
          featureArea: FeatureArea.EXTERNAL_CARD_DETAILS,
          responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
          severity: ErrorSeverity.LOW,
        });
      } finally {
        setThreeDsLoading(false);
      }
    } else {
      try {
        setIsVerifyCardScreenShown(false);
        if (isReady) {
          setThreeDsLoading(true);
          const success = await startThreeDsWebFlow(
            ProcessTypeInput.CardUpdation,
            flowName,
            cardId,
          );
          if (success) {
            setIsVerifyCardScreenShown(false);
            await updateExpiryDate(cardId, expiryDate.toString(), from);
          }
        }
      } catch (error) {
        logErrorWithContext(error, {
          featureArea: FeatureArea.EXTERNAL_CARD_DETAILS,
          responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
          severity: ErrorSeverity.LOW,
        });
      } finally {
        setThreeDsLoading(false);
      }
    }
  };

  const { segmentClient } = useAnalyticsConfiguration();

  useLogGQLErrorScreenAnalytics(
    combinedErrorScreen?.analytics,
    'Update Expiry Date',
    flowName,
  );

  const handleErrorButtonPress = (button?: ButtonComponentV2) => {
    const action = button?.buttonOnClick;

    switch (action?.__typename) {
      case ButtonComponentActionResultTypenames.RouteNavigationAction:
        router.replace(action.route as Href);
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        break;

      case ButtonComponentActionResultTypenames.SDContinueClientFlowAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        if (errorScreen) {
          const { expiryDate } = getValues();
          void updateExpiryDate(cardId, expiryDate.toString());
        } else {
          void onSubmit();
        }
        break;

      case ButtonComponentActionResultTypenames.SDBackNavigationAction:
        if (action.analyticsEvent) {
          const { name, properties } = parseGQLAnalyticsEvent(
            action.analyticsEvent,
          );
          void segmentClient.logEvent(name, {
            ...properties,
            flow_name: flowName,
          });
        }
        if (button?.id === 'EXIT_BUTTON') {
          if (
            webError?.processType === ProcessTypeInput.Transaction ||
            errorScreenThreeDs?.processType === ProcessTypeInput.Transaction
          ) {
            void cancelTabapayTransfer(
              {
                transactionId: initTransactionId.trim() || initTransactionIdWeb,
              },
              flowName,
            );
          } else {
            navigateBack();
          }
        } else {
          navigateBack();
        }
        break;
    }
  };

  const { externalCardDetails, fetching, refreshing } =
    useExternalCardDetails();

  const verificationScreen = externalCardDetails?.verifyCardActionScreen;

  const [isVerifyCardScreenShown, setIsVerifyCardScreenShown] =
    useState<boolean>(false);

  const shouldShowLoader =
    isLoading || threeDsLoading || webLoading || fetching || refreshing;
  const shouldShowBackButton =
    !(isWeb && combinedErrorScreen) &&
    !combinedErrorScreen?.is3DSError &&
    !shouldShowLoader;

  return isVerifyCardScreenShown && verificationScreen ? (
    <VerifyCardScreen
      title={verificationScreen.title}
      contentText={verificationScreen.message}
      buttonLabel={verificationScreen.button.buttonText}
      isLoading={threeDsLoading}
      onBackClick={() => {
        setIsVerifyCardScreenShown(false);
      }}
      tabaPayEntryFlowType={tabaPayEntryFlowType}
      onContinue={onSubmit}
      illustrationKey={verificationScreen?.image as IllustrationKeyType}
    />
  ) : (
    <>
      <ScreenTransitionOverlay
        isLoading={shouldShowLoader}
        LoadingComponent={
          <View style={styles.loader} accessibilityLabel="loading indicator">
            <FullScreenLoading isCoinLoader />
          </View>
        }
        ErrorComponent={
          combinedErrorScreen ? (
            <View style={styles.errorHeader}>
              <View style={[styles.closeIconWrapper, { top: top }]}>
                <IconButton
                  iconKey="CLOSE"
                  iconSize={24}
                  iconOnPress={() => {
                    if (
                      (isUserCancelled3DS(combinedErrorScreen) &&
                        webError?.processType ===
                          ProcessTypeInput.Transaction) ||
                      errorScreenThreeDs?.processType ===
                        ProcessTypeInput.Transaction
                    ) {
                      void cancelTabapayTransfer(
                        {
                          transactionId:
                            initTransactionId.trim() || initTransactionIdWeb,
                        },
                        flowName,
                      );
                    } else {
                      navigateBack();
                    }
                  }}
                  iconButtonAccessibilityLabel="close"
                />
              </View>
              <ErrorScreen
                screenType={ErrorScreenType.Error}
                messageHeadline={combinedErrorScreen.messageHeadline}
                messageBody={combinedErrorScreen.messageBody}
                primaryButton={{
                  title:
                    combinedErrorScreen.primaryButton?.buttonText ||
                    translatedText('closeButton'),
                  onPress: () => {
                    if (combinedErrorScreen.primaryButton?.buttonText) {
                      handleErrorButtonPress(combinedErrorScreen.primaryButton);
                    } else {
                      navigateBack();
                    }
                  },
                }}
                secondaryButton={
                  combinedErrorScreen.secondaryButton
                    ? {
                        title: combinedErrorScreen.secondaryButton.buttonText,
                        isLoading: cancelTransferLoading,
                        onPress: () =>
                          handleErrorButtonPress(
                            combinedErrorScreen.secondaryButton,
                          ),
                      }
                    : undefined
                }
                screenAnalyticsEventKey="update-card-expiration-date-error"
                customAttributes={{
                  flowName: flowNameForAnalytics,
                }}
              />
            </View>
          ) : null
        }
      />
      <View style={styles.container} testID="inline-loader">
        <KeyboardAvoidingView
          style={styles.keyboardContainer}
          behavior={isIOS ? 'padding' : 'height'}
          keyboardVerticalOffset={getKeyboardVerticalOffset(
            height,
            isKeyboardVisible,
          )}
        >
          <Stack.Screen
            options={{
              headerShown: shouldShowBackButton,
              headerBackVisible: shouldShowBackButton,
              title: translatedText('title'),
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar {...props} primaryOnPress={navigateBack} />
              ),
            }}
          />
          <ScrollView contentContainerStyle={styles.keyboardContainer}>
            <View style={styles.dobFormContainer}>
              <BodyBaseText numberOfLines={1}>
                {translatedText('subtitle')}
              </BodyBaseText>
              <Controller
                control={control}
                name="expiryDate"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <View style={styles.input}>
                      <VXLTextInput
                        errorText={errors.expiryDate?.message ?? ''}
                        onBlur={() => {
                          handleExpiryDateErrorTriggered();
                          onBlur();
                        }}
                        onChangeText={(text) =>
                          onChange(formatCardExpirationDate(text))
                        }
                        placeholder="MM/YY"
                        keyboardType="numeric"
                        inputMode="numeric"
                        value={value}
                        maxLength={5}
                        testID="text-input-expiryDate"
                      />
                    </View>
                  );
                }}
              />
            </View>
          </ScrollView>
          <View
            style={{
              paddingBottom: bottom,
            }}
          >
            <Button
              label={translatedText('buttonLabel')}
              onPress={() => setIsVerifyCardScreenShown(true)}
              isLoading={threeDsLoading}
              size="LARGE"
              type="PRIMARY"
              width="FULL"
              isDisabled={!isValid}
              analyticsId="CardExpirationDateUpdateRequested"
              customAttributes={{
                flow_name: flowNameForAnalytics,
              }}
            />
          </View>
        </KeyboardAvoidingView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  closeIconWrapper: {
    left: theme.spacing.xs,
    position: 'absolute',
    zIndex: 10,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    display: 'flex',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: '100%',
    padding: theme.spacing.m,
  },
  dobFormContainer: {
    display: 'flex',
    flex: 1,
    flexGrow: 1,
    justifyContent: 'flex-start',
    width: '100%',
  },
  errorHeader: {
    flex: 1,
    height: '100%',
    width: '100%',
  },
  input: {
    borderRadius: theme.spacing.xs,
    height: 'auto',
    marginTop: theme.spacing.m,
    width: '100%',
  },
  keyboardContainer: {
    flex: 1,
  },
  loader: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
});
